import { Button, Stack, Typography, useTheme } from "@mui/material";
import { ChevronDown, ChevronUp, Clock, Percent } from "lucide-react";
import type { FC } from "react";
import { LoanCalculatorSliderRow } from "web_myplann/Goals/Components/LoanCalculator/LoanCalculatorSliderRow";
import { LoanCalculatorSummary } from "web_myplann/Goals/Components/LoanCalculator/LoanCalculatorSummary";
import {
	LOAN_RATE_MAX,
	LOAN_RATE_MIN,
	LOAN_RATE_STEP,
	LOAN_TERM_STEP,
} from "web_myplann/Goals/constants";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import { useLoanCalculator } from "web_myplann/Goals/Hooks/useLoanCalculator";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { CollapsibleSection } from "web_myplann/Shared/Components/CollapsibleSection";
import { formatRate } from "web_myplann/Shared/Utils/percentageFormatters";

type Props = {
	goal: GoalDetail;
};

export const GoalDetailLoanCalculator: FC<Props> = ({ goal }) => {
	const theme = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();

	const {
		isMortgage,
		rate,
		setRate,
		termYears,
		setTermYears,
		termMin,
		termMax,
		collapsed,
		toggleCollapsed,
		monthlyPayment,
		totalPaid,
		overpay,
	} = useLoanCalculator(goal);

	if (goal.completed || goal.loanAmount <= 0) return null;

	const title = isMortgage
		? t(Resources.Goals.Detail.LoanCalculator.titleMortgage, {
				amount: formatAmount(goal.loanAmount),
			})
		: t(Resources.Goals.Detail.LoanCalculator.titleLoan, {
				amount: formatAmount(goal.loanAmount),
			});
	const description = isMortgage
		? t(Resources.Goals.Detail.LoanCalculator.descriptionMortgage)
		: t(Resources.Goals.Detail.LoanCalculator.descriptionLoan);
	const minTermLabel = t(Resources.Goals.Detail.LoanCalculator.termValue, {
		count: termMin,
	});
	const maxTermLabel = t(Resources.Goals.Detail.LoanCalculator.termValue, {
		count: termMax,
	});

	return (
		<CollapsibleSection title={title}>
			<Typography
				variant="caption"
				component="p"
				color={theme.palette.grey[500]}
				px={1.6}
				pb={1}
				lineHeight={1.5}
			>
				{description}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				{!collapsed && (
					<>
						<LoanCalculatorSliderRow
							icon={
								<Percent
									size={theme.iconSize.sm}
									color={theme.palette.grey[400]}
								/>
							}
							label={t(Resources.Goals.Detail.LoanCalculator.rateLabel)}
							valueLabel={t(Resources.Goals.Detail.LoanCalculator.rateValue, {
								rate: formatRate(rate, 1),
							})}
							value={rate}
							onChange={setRate}
							min={LOAN_RATE_MIN}
							max={LOAN_RATE_MAX}
							step={LOAN_RATE_STEP}
							minLabel={t(Resources.Goals.Detail.LoanCalculator.rateMin)}
							maxLabel={t(Resources.Goals.Detail.LoanCalculator.rateMax)}
						/>
						<LoanCalculatorSliderRow
							icon={
								<Clock
									size={theme.iconSize.sm}
									color={theme.palette.grey[400]}
								/>
							}
							label={t(Resources.Goals.Detail.LoanCalculator.termLabel)}
							valueLabel={t(Resources.Goals.Detail.LoanCalculator.termValue, {
								count: termYears,
							})}
							value={termYears}
							onChange={setTermYears}
							min={termMin}
							max={termMax}
							step={LOAN_TERM_STEP}
							minLabel={minTermLabel}
							maxLabel={maxTermLabel}
						/>
					</>
				)}

				<LoanCalculatorSummary
					rate={rate}
					termYears={termYears}
					monthlyPayment={monthlyPayment}
					totalPaid={totalPaid}
					overpay={overpay}
				/>

				<Button
					variant="inlineAction"
					disableRipple
					onClick={toggleCollapsed}
					sx={{ borderTop: 1, borderColor: "surface.border" }}
				>
					{collapsed ? (
						<>
							<ChevronDown size={14} />
							{t(Resources.Goals.Detail.LoanCalculator.showDetail)}
						</>
					) : (
						<>
							<ChevronUp size={14} />
							{t(Resources.Goals.Detail.LoanCalculator.hideDetail)}
						</>
					)}
				</Button>
			</Stack>
		</CollapsibleSection>
	);
};
